/* ----------------------------------------------------------------------------
 * Deterministic mock STT — zero network, node-testable, local-demo safe.
 *
 * `simulateUtterance(text)` emits one interim callback per accumulated word
 * then a single final callback, deterministically (no real timers, no audio).
 * This lets the state-machine round-trip be proven offline and lets the beta
 * UI run with no credentials. `finish()` is a no-op for the mock (the demo/test
 * drives utterances via simulateUtterance).
 *
 * NOTE: no TS "parameter properties" are used anywhere in the voice modules
 * that run under Node's native (strip-only) test runner — fields are declared
 * and assigned explicitly.
 * -------------------------------------------------------------------------- */

import type { SttAdapter, SttOpenOptions, SttStream, SttStreamHandlers } from './SttAdapter.ts';

class MockSttStream implements SttStream {
  readonly provider = 'mock';
  private closed = false;
  private readonly handlers: SttStreamHandlers;

  constructor(handlers: SttStreamHandlers) {
    this.handlers = handlers;
  }

  /** Deterministically stream `text` as interim words then a final segment. */
  simulateUtterance(text: string): void {
    if (this.closed) return;
    const words = text.split(/\s+/).filter((w) => w.length > 0);
    let acc = '';
    for (const w of words) {
      acc = acc === '' ? w : `${acc} ${w}`;
      this.handlers.onInterim?.(acc);
    }
    this.handlers.onFinal?.(acc);
  }

  /** No-op: mock utterances are driven by simulateUtterance(), not real audio. */
  async finish(): Promise<void> {
    /* nothing to transcribe — the demo/test path calls simulateUtterance(). */
  }

  async close(): Promise<void> {
    this.closed = true;
  }
}

export class MockSttAdapter implements SttAdapter {
  readonly provider = 'mock';

  async openStream(_opts: SttOpenOptions, handlers: SttStreamHandlers): Promise<SttStream> {
    void _opts;
    return new MockSttStream(handlers);
  }
}

export { MockSttStream };
